import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {useLicenseTypes} from 'src/hooks/dictionaries';
import {accountSearchResultResultSelector, selectSelectedColumns} from 'src/modules/account-search-result';
import {getAccountsByIds} from 'src/openapi/admin/apis/GetAccountsApi';
import {useAppDispatch} from 'src/store/hooks';

import {COLUMNS, HEADERS} from '../../constants';
import {useGetColumnValue} from '../../hooks/useGetColumnValue';

import {ACCOUNT_DOWNLOAD_FILE_NAME, ACCOUNT_DOWNLOAD_TASK_NAME} from './constants';
import {fetchCrmIntegrations} from './utils/fetchCrmIntegrations';
import {fetchLicenses} from './utils/fetchLicenses';
import {fetchParentAccountNames} from './utils/fetchParentAccountNames';
import {fetchPrimaryManagers} from './utils/fetchPrimaryManagers';
import {fetchSsoData} from './utils/fetchSsoData';
import {fetchUsersCountByState} from './utils/fetchUsersCountByState';


import type {GetData} from 'src/components/chuncked-download-tool/typedef';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    accountIds: number[],
    children: (handler: () => void) => React.ReactNode,
    getSortedEntityIds: () => Promise<{
        type: string,
        payload: {
            data: number[] | string[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const AccountSearchDownloadTool = ({accountIds, children, getSortedEntityIds}: Props) => {
    const apiDispatch = useAppDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);
    const result = useSelector(accountSearchResultResultSelector);

    const {getLicenseTypeByCode} = useLicenseTypes();
    const {getColumnValue} = useGetColumnValue();

    const fileHeaders = useMemo(() => {
        return selectedColumns.reduce((acc, curr) => ({...acc, [curr]: HEADERS[curr]}), {});
    }, [selectedColumns]);

    const getData: GetData<Record<string, string>> = useCallback(async (selectedIds: number[]) => {
        let data: Record<string, string>[] = [];
        let fetchingFailed = false;
        let nameToIdsMap: Record<string, number> = {};

        let ids: number[] = [];

        if (result && result.totalCount < MAX_SELECTED_ENTITIES) {
            const sortedEntityIdsResponse = await getSortedEntityIds();
            if (sortedEntityIdsResponse.payload.success) {
                const sortedEntityIds = sortedEntityIdsResponse.payload.data as number[];
                ids = sortedEntityIds.filter(id => selectedIds.includes(id));
            } else {
                fetchingFailed = true;
            }
        } else {
            ids = selectedIds;
        }

        const baseResults = await apiDispatch(getAccountsByIds({requestBody: ids}));
        if (baseResults.payload.success) {
            const baseResultsResponse = baseResults.payload.data;
            const accounts = ids.map(id => baseResultsResponse[id]);

            nameToIdsMap = accounts.reduce((acc, curr) => ({...acc, [curr.accountName]: curr.accountId}), {});

            data = accounts.map(account => selectedColumns.reduce((acc, curr) => ({
                ...acc,
                [curr]: getColumnValue(curr, account),
            }), {}));

            const parentResult = await fetchParentAccountNames({
                data, accounts, selectedColumns, apiDispatch,
            });
            if (parentResult === null) {
                fetchingFailed = true;
            } else {
                data = parentResult;
            }

            const updatedData = await fetchUsersCountByState({
                data,
                selectedColumns,
                accountIds,
                nameToIdsMap,
                apiDispatch,
            });

            if (updatedData === null) {
                fetchingFailed = true;
            } else {
                data = updatedData;
            }


            const crmData = await fetchCrmIntegrations({
                data,
                selectedColumns,
                accountIds,
                nameToIdsMap,
                apiDispatch,
            });

            if (crmData === null) {
                fetchingFailed = true;
            } else {
                data = crmData;
            }

            const ssoData = await fetchSsoData({
                data,
                selectedColumns,
                accountIds,
                nameToIdsMap,
                apiDispatch,
            });

            if (ssoData === null) {
                fetchingFailed = true;
            } else {
                data = ssoData;
            }

            const managerData = await fetchPrimaryManagers({
                data,
                selectedColumns,
                accountIds,
                nameToIdsMap,
                apiDispatch,
            });

            if (managerData === null) {
                fetchingFailed = true;
            } else {
                data = managerData;
            }

            const licenseData = await fetchLicenses({
                data,
                selectedColumns,
                accountIds,
                nameToIdsMap,
                getLicenseTypeByCode,
                apiDispatch,
            });

            if (licenseData === null) {
                fetchingFailed = true;
            } else {
                data = licenseData;
            }
        } else {
            fetchingFailed = true;
        }

        if (!fetchingFailed) {
            const idToRowMap = new Map<number, Record<string, string>>();
            data.forEach(row => {
                const id = nameToIdsMap[row[COLUMNS.ACCOUNT_NAME]];
                if (id != null) {
                    idToRowMap.set(id, row);
                }
            });

            data = selectedIds
                .map(id => idToRowMap.get(id))
                .filter((row): row is Record<string, string> => !!row);

            return Promise.resolve({type: '', payload: {success: true, error: false, data}});
        } else {
            return Promise.resolve({
                type: '', payload: {
                    success: false,
                    error: true,
                    data: {message: 'Failed to fetch data for the report'},
                },
            });
        }
    }, [
        apiDispatch,
        getLicenseTypeByCode,
        result,
        selectedColumns,
        getColumnValue,
        getSortedEntityIds,
        accountIds,
    ]);

    return (
        <ChunkedDownloadTool
            ids={accountIds}
            taskName={ACCOUNT_DOWNLOAD_TASK_NAME}
            fileName={ACCOUNT_DOWNLOAD_FILE_NAME}
            fileHeaders={fileHeaders}
            getData={getData}
            chunkSize={250}>
            {handler => children(handler)}
        </ChunkedDownloadTool>
    );
};
